import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./SecretChart.css";
import { formatDate, formatDateWbar, formatAccessWbar } from "../../../../components/Util/DateFormat";
import axios from "axios";
import Swal from "sweetalert2";
import { getServerBaseUrl } from "../../../../utils/portConfig";

function SecretChart({ patientData }) {
  // 초기 비밀 차트 데이터
  const initialSecretData = {
    id: "",
    patientId: "",
    comments: "",
  };

  // State 변수 선언
  const [patientId, setPatientId] = useState("");
  const [secretData, setSecretData] = useState({ ...initialSecretData });
  const [todayDateStatus, setTodayDateStatus] = useState(false);
  const [patientDateCategories, setPatientDateCategories] = useState([]);
  const navigate = useNavigate();

  const API_SERVER_URL = `${getServerBaseUrl()}/api/v1`;
  const token = localStorage.getItem("token");

  // 환자 데이터가 변경될 때 실행되는 useEffect
  useEffect(() => {
    if (patientData) {
      setPatientId(patientData != null ? patientData.id : null);
      setSecretData({ ...initialSecretData });
    }
  }, [patientData]);

  // 환자 ID가 변경될 때 실행되는 useEffect
  useEffect(() => {
    if (patientId) {
      getPatientDateCategories();
    }
  }, [patientId]);

  // 환자 진단 날짜 카테고리를 가져오는 함수
  const getPatientDateCategories = async () => {
    try {
      const response = await axios.get(`${API_SERVER_URL}/schart/${patientId}/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const patientDateData = response.data.map((item) => ({
        value: item.code,
        label: formatAccessWbar(item.modifiedDt),
      }));

      const chkTodayDate = patientDateData.find((item) => item.label === formatDate(new Date()).replace(".", "-").replace(".", "-"));
      if (chkTodayDate) {
        setTodayDateStatus(true);
      }

      setPatientDateCategories(patientDateData);

      // 선택된 환자 진단 날짜에 해당하는 비밀차트 정보를 가져오는 함수
      const detailedDataPromises = patientDateData.map(async (item) => {
        try {
          const detailedResponse = await axios.get(`${API_SERVER_URL}/schart/${patientId}/${item.value}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          return {
            [item.value]: detailedResponse.data,
          };
        } catch (error) {
          console.error(`Error fetching detailed data for ${item.value}:`, error);
          return {
            [item.value]: null, // Handle the error case as needed
          };
        }
      });

      // Wait for all detailed data requests to complete
      const detailedDataArray = await Promise.all(detailedDataPromises);

      // Combine all detailed data into a single object
      const combinedDetailedData = detailedDataArray.reduce((accumulator, currentData) => {
        return { ...accumulator, ...currentData };
      }, {});

      // Set the accumulated detailed data using setSecretData
      setSecretData({ ...combinedDetailedData });
    } catch (error) {
      console.error("Patient Date Category fetch error:", error);
    }
  };

  // 비밀차트 - 등록버튼
  const secretRegDateButton = () => {
    const maxValue = Math.max(...patientDateCategories.map((item) => parseInt(item.value, 10) || 0));
    const newPatientDateValue = maxValue + 1;

    const currentDate = formatAccessWbar(new Date());
    const isDateExists = patientDateCategories.find((item) => item.label === currentDate);
    if (isDateExists) {
      showWarningAlert("이미 금일 차트가 존재합니다.");
      return;
    } else {
      const newPatientDateEntry = {
        value: newPatientDateValue.toString(),
        label: currentDate,
      };

      setPatientDateCategories([...patientDateCategories, newPatientDateEntry]);
      navigate("/", { state: { gotoEditPage: "등록" } });
    }
  };

  // 비밀차트 - 수정 버튼
  const secretModifyDateButton = (data, patientDate) => {
    navigate("/", { state: { gotoEditPage: "수정", sData: data, chartDate: patientDate } });
  };

  // 비밀차트 - 삭제 버튼
  const secretDeleteChartButton = async (chartId, chartDate) => {
    const confirmationResult = await Swal.fire({
      icon: "question",
      title: "비밀차트 삭제",
      text: "비밀차트를 삭제하시겠습니까?",
      showCancelButton: true,
      confirmButtonText: "확인",
      cancelButtonText: "취소",
      customClass: {
        popup: "custom-swal-popup",
      },
    });

    if (confirmationResult.isConfirmed) {
      const formattedTodayDate = formatDateWbar(new Date());
      if (chartDate === formattedTodayDate) {
        console.log("등록된 오늘 날짜의 차트를 삭제합니다.");
        setTodayDateStatus(false);
      }
      try {
        const response = await axios.delete(`${API_SERVER_URL}/schart/` + chartId + "/delete", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.status === 204) {
          showSuccessAlert("차트가 삭제되었습니다.");
          setSecretData({ ...initialSecretData });
          getPatientDateCategories();
        } else {
          showErrorAlert("실패", "차트 삭제 실패. 다시 시도해주세요.");
        }
      } catch (error) {
        console.log("Error deleting chart:", error);
        if (error.response) {
          const responseData = error.response.data;
          if (responseData) {
            showErrorAlert("차트 삭제 오류", responseData);
          } else {
            showErrorAlert("차트 삭제 오류", `서버 응답 오류: ${error.response.status}`);
          }
        } else {
          showErrorAlert("서버 응답이 없습니다.", "서버 응답이 없습니다.");
        }
      }
    }
  };

  /* 메세지 공통 함수*/
  const showSuccessAlert = (text) => {
    Swal.fire({
      icon: "success",
      title: "성공",
      text: text,
      customClass: {
        popup: "custom-swal-popup",
      },
    });
  };

  const showErrorAlert = (title, text) => {
    Swal.fire({
      icon: "error",
      title: title,
      text: text,
      customClass: {
        popup: "custom-swal-popup",
      },
    });
  };

  const showWarningAlert = (text) => {
    Swal.fire({
      icon: "warning",
      title: "알림",
      text: text,
      customClass: {
        popup: "custom-swal-popup",
      },
    });
  };

  return (
    <div className="secret-container">
      <div className="secret-scrollable-area">
        {patientDateCategories.length === 0 ? (
          <div className="gosiWrapper-guide">
            <p>작성한 내용이 없습니다.</p>
          </div>
        ) : (
          patientDateCategories.map((patientDate, pIndex) => (
            <div className="secret-item-group" key={pIndex}>
              <div className="checklist-item-date">{patientDate.label}</div>
              {patientDate.value && secretData[patientDate.value] && (
                <div className="secret-item">
                  <div className="secret-item-contents align-left" dangerouslySetInnerHTML={{ __html: secretData[patientDate.value].comments }}></div>
                  <div className="secret-item-result">
                    <button onClick={() => secretModifyDateButton(secretData[patientDate.value], patientDate.label)}>수정</button>
                    <button className="secret-delete-button" onClick={() => secretDeleteChartButton(patientDate.value, patientDate.label)}>
                      삭제
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
      <div className="secret-submit-button-container">
        <button type="button" className="secret-reg-button" onClick={secretRegDateButton}>
          등록
        </button>
      </div>
    </div>
  );

  // return (
  //   <div className='secret-container'>
  //     <div className="secret-scrollable-area">
  //         {patientDateCategories.map((patientDate, pIndex) => (
  //           <div className='secret-item-group' key={pIndex}>
  //             <div className="checklist-item-date">
  //               {patientDate.label}
  //             </div>
  //             {patientDate.value && secretData[patientDate.value] && (
  //               <div className="secret-item">
  //                 <div className='secret-item-contents align-left' dangerouslySetInnerHTML={{__html: secretData[patientDate.value].comments}}></div>
  //                 <div className='secret-item-result'>
  //                   <button onClick={() => secretModifyDateButton(secretData[patientDate.value], patientDate.label)}>수정</button>
  //                   <button className='secret-delete-button' onClick={() => secretDeleteChartButton(patientDate.value, patientDate.label)}>삭제</button>
  //                 </div>
  //               </div>
  //             )}
  //           </div>
  //         ))}
  //       </div>
  //       <div className='submit-button-container'>
  //         <button type='button' className='secret-reg-button' onClick={secretRegDateButton}>등록</button>
  //       </div>
  //     </div>
  // );
}

export default SecretChart;
